// src/components/PaymentForm.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const PaymentForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: '',
    service: '',
    description: '',
    checkoutType: '3_6_12_months'
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const checkoutTypeMapping = {
    '3_6_12_months': 'chktAQFM781601',
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    
    value = value.replace(/[^0-9.]/g, '');
    
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].slice(0, 2);
    }

    setFormData(prev => ({
      ...prev,
      amount: value
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatAmount = (value) => {
    if (!value) return '';
    const number = parseFloat(value);
    if (isNaN(number)) return '';
    return number.toFixed(2);
  };

  const generateShortId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopyStatus('Link copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (err) {
      setCopyStatus('Error copying link');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formattedAmount = formatAmount(formData.amount);
    if (!formattedAmount || formattedAmount === '0.00') {
      alert('Please enter a valid amount');
      return;
    }

    setLoading(true);

    try {
      const shortId = generateShortId();
      
      const paymentData = {
        short_id: shortId,
        amount: formattedAmount,
        transaction_id: 'TXN-' + Date.now(),
        service: formData.service,
        description: formData.description,
        checkout_type: formData.checkoutType,
        checkout_id: checkoutTypeMapping[formData.checkoutType],
        status: 'pending',
        attempts: 0
      };

      const { data, error } = await supabase
        .from('payments')
        .insert([paymentData])
        .select()
        .single();

      if (error) throw error;

      const checkoutUrl = `${window.location.origin}${window.location.pathname}#/checkout/${shortId}`;
      setGeneratedLink(checkoutUrl);
    } catch (error) {
      console.error('Error:', error);
      setCopyStatus('Error generating link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-center text-gray-800">
            Generate Payment Link
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
              Amount (CAD) *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-500">$</span>
              <input
                id="amount"
                name="amount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                required
                className="w-full pl-8 pr-16 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={formData.amount}
                onChange={handleAmountChange}
                onBlur={(e) => {
                  const formatted = formatAmount(e.target.value);
                  setFormData(prev => ({ ...prev, amount: formatted }));
                }}
              />
              <span className="absolute right-3 top-2.5 text-gray-500 text-sm">CAD</span>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="service" className="block text-sm font-medium text-gray-700">
              Service *
            </label>
            <input
              id="service"
              name="service"
              type="text"
              required
              placeholder="Service name"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={formData.service}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows="3"
              placeholder="Additional details..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={formData.description}
              onChange={handleInputChange}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="checkoutType" className="block text-sm font-medium text-gray-700">
              Payment Options *
            </label>
            <select
              id="checkoutType"
              name="checkoutType"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
              value={formData.checkoutType}
              onChange={handleInputChange}
            >
              <option value="3_6_12_months">Enable 3 ,6 and 12 month payments </option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Generating...' : 'Generate Payment Link'}
          </button>
        </form>

        {generatedLink && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              Payment Link:
            </h3>
            <div className="relative">
              <input
                type="text"
                readOnly
                value={generatedLink}
                className="w-full p-3 bg-white border border-gray-300 rounded-md pr-24 text-sm"
              />
              <button
                onClick={copyToClipboard}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Copy
              </button>
            </div>
            {copyStatus && (
              <p className="mt-2 text-sm text-center text-green-600">
                {copyStatus}
              </p>
            )}
            <div className="mt-4 flex space-x-4">
              <button
                onClick={() => window.open(generatedLink, '_blank')}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition duration-300"
              >
                Open Link
              </button>
              <button
                onClick={() => navigate(generatedLink.split('#')[1])}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition duration-300"
              >
                Go to Checkout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentForm;